import React, { useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  Center,
  HStack,
  Text,
  useTheme,
  VStack,
} from '@chakra-ui/react'
import { Formik } from 'formik'
import { REPOSITORY } from '../../api/repository/Repository'
import InputForm from '../../components/InputForm/InputForm'
import SelectorField from '../../components/Selector/SelectorField'
import UnderlineHeading from '../../components/UnderlineText/UnderlineHeading'
import { useLabels } from '../../utils/hooks/useLabels/useLabels'
import { defaultOptions } from '../RechargeForm/FormFactory/Forms/Discount/IndividualDiscount'
import { useNavigate } from 'react-router-dom'
import { useCompanyData } from '../../utils/hooks/company/useCompanyData'
import { useLoadingContext } from '../../utils/hooks/Context/LoadingContext'
import { useRequestStatusContext } from '../../utils/hooks/Context/RequestStatusContext'

export const useRegions = () => {
  const regions = REPOSITORY.local.company.getRegions()

  const formattedRegions = useMemo(
    () =>
      regions?.map((element) => ({
        ...element,
        value: element.idRegion,
        id: element.idRegion,
        label: element.NombreRegion,
      })) || [],
    [regions],
  )

  const foundedCommunes = (region) => {
    const found = regions.find((element) => element.idRegion === region)
    return found
      ? found?.oComunas?.map((element) => ({
          ...element,
          value: element.idComuna,
          id: element.idComuna,
          label: element.NombreComuna,
        }))
      : []
  }

  return {
    regions: formattedRegions,
    foundedCommunes,
  }
}

export default function NewBranchOffice() {
  const { sCentroCosto } = REPOSITORY.local.company.getCompanyData()
  const { sEmpresa } = REPOSITORY.local.company.getCompanyData()
  const {
    colors: { gray, log },
  } = useTheme()
  const navigate = useNavigate()
  const { searchCompanyData } = useCompanyData()
  const { regions, foundedCommunes } = useRegions()
  const { handleOpen: handleOpenLoading, handleClose: handleCLoseLoading } =
    useLoadingContext()
  const { handleOpen, handleSetTitle, handleChangeBody, handleClose } =
    useRequestStatusContext()

  const { texts } = useLabels()

  const onBack = useCallback(() => {
    navigate(-1)
  }, [])

  const onSubmit = async (values, { resetForm }) => {
    handleOpenLoading()
    await REPOSITORY.wsEmpresa.company
      .createBranchOffice({
        sEmpresa,
        NombreSucursal: values.NombreSucursal,
        sRegion: values.sRegion,
        sDireccion: values.sDireccion,
        sComuna: values.sComuna,
        sCentroCosto: values.sCentroCosto,
        InfoContacto: [
          {
            NombreContacto: values.NombreContacto,
            TelefonoContacto: values.TelefonoContacto,
            EmailContacto: values.EmailContacto,
          },
        ],
      })
      .then((res) => {
        handleCLoseLoading()
        resetForm()
        handleSetTitle(res.data.sMensaje, res.status)
        handleChangeBody(
          <Button
            variant="primary"
            onClick={() => {
              handleClose()
            }}
          >
            {res.status === 200 ? 'Continuar' : 'Contactar a soporte ahora'}
          </Button>,
        )
        handleOpen()
      })
      .catch((error) => {
        handleCLoseLoading()
        handleSetTitle(error.response.data.error, error.response.status)
        handleChangeBody(
          <Button
            variant="primary"
            onClick={() => {
              handleClose()
            }}
          >
            Contactar a soporte ahora
          </Button>,
        )
        handleOpen()
      })
    searchCompanyData(sEmpresa)
    handleCLoseLoading()
  }
  return (
    <Formik
      initialValues={{
        NombreSucursal: '',
        sRegion: '',
        sDireccion: '',
        sComuna: '',
        sCentroCosto: '',
        NombreContacto: '',
        TelefonoContacto: '',
        EmailContacto: '',
      }}
      onSubmit={onSubmit}
      validate={(values) => {
        const errors = {}
        Object.keys(values).forEach((key) => {
          if (
            (!values[key] && key !== 'sCentroCosto') ||
            (key === 'sCentroCosto' && values.sCentroCosto === null)
          ) {
            errors[key] = 'Requerido'
          }
        })
        return errors
      }}
      validateOnMount
      validateOnBlur
      // validateOnChange
    >
      {({ values, handleChange, handleSubmit, isValid, errors, touched }) => (
        <VStack spacing="32px" h="90vh" alignItems="flex-start">
          <UnderlineHeading
            text="Datos de Sucursal"
            variant="h5"
            color={gray['800']}
          />
          <HStack alignItems="flex-start" w="75%" spacing="30px">
            <VStack w="100%" spacing="30px" alignItems="flex-start">
              <VStack alignItems="flex-start" w="100%">
                <InputForm
                  stackProps={{ w: '100%' }}
                  name="NombreSucursal"
                  id="NombreSucursal"
                  title={'Nombre sucursal'}
                  variant="subtitle3"
                  value={values?.NombreSucursal}
                  onChange={handleChange}
                />
                {errors.NombreSucursal && touched.NombreSucursal ? (
                  <Box>
                    <Text color={log.error} variant="body3">
                      {errors.NombreSucursal}
                    </Text>
                  </Box>
                ) : null}
              </VStack>

              {/* <InfoLabel
                title={'Nombre sucursal'}
                value={values?.NombreSucursal}
              /> */}
              <VStack alignItems="flex-start" w="100%">
                <SelectorField
                  isClearable
                  placeholder="Seleccionar Región"
                  options={regions || []}
                  id="sRegion"
                  name="sRegion"
                  onChange={handleChange}
                  value={values.sRegion}
                  title={'Región'}
                  titleType="subtitle3"
                />
                {errors.sRegion && touched.sRegion ? (
                  <Box>
                    <Text color={log.error} variant="body3">
                      {errors.sRegion}
                    </Text>
                  </Box>
                ) : null}
              </VStack>
              <VStack alignItems="flex-start" w="100%">
                <InputForm
                  stackProps={{ w: '100%' }}
                  name="sDireccion"
                  id="sDireccion"
                  variant="subtitle3"
                  title={'Dirección'}
                  value={values?.sDireccion}
                  onChange={handleChange}
                />
                {errors.sDireccion && touched.sDireccion ? (
                  <Box>
                    <Text color={log.error} variant="body3">
                      {errors.sDireccion}
                    </Text>
                  </Box>
                ) : null}
              </VStack>
              <VStack alignItems="flex-start" w="100%">
                <SelectorField
                  isClearable
                  placeholder="Seleccionar Región"
                  options={foundedCommunes(values.sRegion)}
                  id="sComuna"
                  name="sComuna"
                  onChange={handleChange}
                  value={values.sComuna}
                  title={'Comuna'}
                  titleType="subtitle3"
                />
                {errors.sComuna && touched.sComuna ? (
                  <Box>
                    <Text color={log.error} variant="body3">
                      {errors.sComuna}
                    </Text>
                  </Box>
                ) : null}
              </VStack>
              <VStack alignItems="flex-start" w="100%">
                <SelectorField
                  value={values.sCentroCosto}
                  options={sCentroCosto || defaultOptions}
                  name="sCentroCosto"
                  title={texts.cost_center}
                  titleType="subtitle3"
                  isClearable
                  placeholder="Seleccionar centro de costo"
                />
                {errors.sCentroCosto && touched.sCentroCosto ? (
                  <Box>
                    <Text color={log.error} variant="body3">
                      {errors.sCentroCosto}
                    </Text>
                  </Box>
                ) : null}
              </VStack>
            </VStack>
            <VStack w="100%" spacing="30px">
              <VStack alignItems="flex-start" w="100%">
                <InputForm
                  stackProps={{ w: '100%' }}
                  name="NombreContacto"
                  id="NombreContacto"
                  variant="subtitle3"
                  title={'Contacto de la sucursal'}
                  value={values?.NombreContacto}
                  onChange={handleChange}
                />
                {errors.NombreContacto && touched.NombreContacto ? (
                  <Box>
                    <Text color={log.error} variant="body3">
                      {errors.NombreContacto}
                    </Text>
                  </Box>
                ) : null}
              </VStack>
              <VStack alignItems="flex-start" w="100%">
                <InputForm
                  stackProps={{ w: '100%' }}
                  name="TelefonoContacto"
                  id="TelefonoContacto"
                  variant="subtitle3"
                  title={'Teléfono de contacto'}
                  value={values?.TelefonoContacto}
                  onChange={handleChange}
                />
                {errors.TelefonoContacto && touched.TelefonoContacto ? (
                  <Box>
                    <Text color={log.error} variant="body3">
                      {errors.TelefonoContacto}
                    </Text>
                  </Box>
                ) : null}
              </VStack>
              <VStack alignItems="flex-start" w="100%">
                <InputForm
                  stackProps={{ w: '100%' }}
                  name="EmailContacto"
                  id="EmailContacto"
                  variant="subtitle3"
                  title={'Correo electrónico de contacto'}
                  value={values?.EmailContacto}
                  onChange={handleChange}
                />
                {errors.EmailContacto && touched.EmailContacto ? (
                  <Box>
                    <Text color={log.error} variant="body3">
                      {errors.EmailContacto}
                    </Text>
                  </Box>
                ) : null}
              </VStack>
            </VStack>
          </HStack>
          <Center mb="20px" mt="10px" w="100%">
            <HStack
              spacing="16px"
              alignSelf="center"
              w="auto"
              h="5vh"
              justifyContent="space-between"
            >
              <Button
                onClick={onBack}
                variant="white"
                width={'144px'}
                height={'56px'}
              >
                Cancelar
              </Button>
              <Button
                width={'160px'}
                height={'56px'}
                disabled={!isValid}
                borderRadius={'8px'}
                bgColor={'#0CC863'}
                onClick={handleSubmit}
                {...{
                  ...(!isValid
                    ? {
                        bgColor: '#DDDDDD',
                        borderColor: 'transparent',
                        color: '#565656',
                      }
                    : {}),
                }}
              >
                Guardar
              </Button>
            </HStack>
          </Center>
        </VStack>
      )}
    </Formik>
  )
}
